import { createRouter, createWebHashHistory } from 'vue-router'
import Login from '../views/Login.vue'
import DashboardLayout from '../components/DashboardLayout.vue'
import WindowSettings from '../views/WindowSettings.vue'
import ScriptConfig from '../views/ScriptConfig.vue'
import ConfigCreateWrapper from '../views/ConfigCreateWrapper.vue'
import ConfigEditorWrapper from '../views/ConfigEditorWrapper.vue'
import DataStats from '../views/DataStats.vue'
import UserManagement from '../views/UserManagement.vue'
import UserOperationLogs from '../views/UserOperationLogs.vue'
import APKManagement from '../views/APKManagement.vue'
import Statistics from '../views/Statistics.vue'
import GlobalSettings from '../views/GlobalSettings.vue'
import AdventureIslandKoreaGlobalSettings from '../views/AdventureIslandKoreaGlobalSettings.vue'
import DesktopLicenseManagement from '../views/DesktopLicenseManagement.vue'
import QuestionBankManagement from '../views/QuestionBankManagement.vue'
import AnswerRecordManagement from '../views/AnswerRecordManagement.vue'
import { canManageQuestionBank } from '../constants/questionBank'

const GAME_KOREA = '冒险岛韩服'

function getStoredUserGame() {
  try {
    return JSON.parse(localStorage.getItem('user') || '{}').game || ''
  } catch {
    return ''
  }
}

const routes = [
  {
    path: '/',
    redirect: '/login'
  },
  {
    path: '/login',
    name: 'Login',
    component: Login,
    meta: {
      requiresGuest: true // 需要未登录状态
    }
  },
  {
    path: '/dashboard',
    component: DashboardLayout,
    meta: {
      requiresAuth: true // 需要登录状态
    },
    children: [
      {
        path: '',
        redirect: to => {
          const user = localStorage.getItem('user')
          if (user) {
            try {
              const parsedUser = JSON.parse(user)
              // 根据用户类型重定向
              if (parsedUser.is_admin === 1 || parsedUser.is_admin === 2) {
                // 超级管理员和代理都进入用户管理
                return '/dashboard/users'
              } else {
                // 普通用户和配置管理员进入窗口设置
                return '/dashboard/windows'
              }
            } catch {
              return '/dashboard/windows'
            }
          }
          return '/dashboard/windows'
        }
      },
      {
        path: 'windows',
        name: 'WindowSettings',
        component: WindowSettings,
        meta: { mobileLayout: 'fill' }
      },
      {
        path: 'scripts',
        name: 'ScriptConfig',
        component: ScriptConfig
      },
      {
        path: 'scripts/create',
        name: 'CreateConfig',
        component: ConfigCreateWrapper
      },
      {
        path: 'scripts/edit/:id',
        name: 'EditConfig',
        component: ConfigEditorWrapper,
        props: true
      },
      {
        path: 'data-stats',
        name: 'DataStats',
        component: DataStats
      },
      {
        path: 'global-settings',
        name: 'GlobalSettings',
        component: GlobalSettings
      },
      {
        path: 'korea-global-settings',
        name: 'KoreaGlobalSettings',
        component: AdventureIslandKoreaGlobalSettings
      },
      {
        path: 'users',
        name: 'UserManagement',
        component: UserManagement
      },
      {
        path: 'users/operation-logs',
        name: 'UserOperationLogs',
        component: UserOperationLogs
      },
      {
        path: 'users/desktop-licenses',
        name: 'DesktopLicenseManagement',
        component: DesktopLicenseManagement
      },
      {
        path: 'question-bank',
        name: 'QuestionBankManagement',
        component: QuestionBankManagement
      },
      {
        path: 'answer-records',
        name: 'AnswerRecordManagement',
        component: AnswerRecordManagement
      },
      {
        path: 'apk',
        name: 'APKManagement',
        component: APKManagement
      },
      {
        path: 'statistics',
        name: 'Statistics',
        component: Statistics
      },
      // 其余后台管理页面（道具/常量/物价/保姆统计/活动制造）已移除
    ]
  }
]

const router = createRouter({
  history: createWebHashHistory(process.env.BASE_URL),
  routes
})

// 路由守卫
router.beforeEach((to, from, next) => {
  const token = localStorage.getItem('token')
  const isLoggedIn = !!token

  // 需要登录的页面
  if (to.meta.requiresAuth && !isLoggedIn) {
    next('/login')
    return
  }

  // 需要未登录状态的页面（如登录页）
  if (to.meta.requiresGuest && isLoggedIn) {
    next('/dashboard')
    return
  }

  // 权限检查
  if (isLoggedIn && to.path.startsWith('/dashboard')) {
    try {
      const userInfo = JSON.parse(localStorage.getItem('user') || '{}')
      const userGame = userInfo.game || ''

      if (to.name === 'GlobalSettings' && userGame === GAME_KOREA) {
        next({ name: 'KoreaGlobalSettings', replace: true })
        return
      }
      if (to.name === 'KoreaGlobalSettings' && userGame !== GAME_KOREA) {
        next({ name: 'GlobalSettings', replace: true })
        return
      }

      if (to.path === '/dashboard/question-bank' && !canManageQuestionBank(userInfo)) {
        next(userInfo.is_admin === 2 ? '/dashboard/users' : '/dashboard/windows')
        return
      }

      // 超级管理员专用页面
      if (userInfo.is_admin !== 1) {
        const adminOnlyPaths = ['/dashboard/apk', '/dashboard/statistics']
        if (adminOnlyPaths.includes(to.path)) {
          next('/dashboard/users')
          return
        }
      }

      // 代理可以访问用户管理页面、操作日志页面和物价统计页面
      if (userInfo.is_admin === 2) {
        const allowedPaths = ['/dashboard', '/dashboard/users', '/dashboard/users/operation-logs', '/dashboard/users/desktop-licenses', '/dashboard/answer-records']
        if (!allowedPaths.includes(to.path) && !(to.path === '/dashboard/question-bank' && canManageQuestionBank(userInfo))) {
          next('/dashboard/users')
          return
        }
      }
    } catch (error) {
      console.error('解析用户信息失败:', error)
    }
  }

  next()
})

export default router 